import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Search, Menu, User, LogOut, LayoutDashboard, MessageSquare, Plus, MapPin, Shield, Bell } from "lucide-react";
import { useState, useRef, useEffect, useCallback } from "react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { ALL_LOCATIONS, US_STATES, locationToSlug } from "@/lib/constants";
import api from "@/lib/api";

// Admin emails - same list as backend bootstrap admins
const ADMIN_EMAILS = ['admin@paradiseborrowed.com', 'fevi@wowebsites.com'];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const searchRef = useRef(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifCount, setNotifCount] = useState(0);

  const fetchUnreadCount = useCallback(() => {
    if (!user) return;
    api.get("/messages/unread-count")
      .then(res => setUnreadCount(res.data.unread_count || 0))
      .catch(() => {});
    api.get("/notifications/unread-count")
      .then(res => setNotifCount(res.data.unread_count || 0))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value.trim().length > 0) {
      const filtered = ALL_LOCATIONS.filter((loc) =>
        loc.name.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 6);
      setFilteredLocations(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  // Generate SEO-friendly URL for location
  const getLocationUrl = (location) => {
    // Check if it's a US location with state info
    if (location.stateSlug && location.citySlug) {
      return `/${location.stateSlug}/${location.citySlug}`;
    }
    // For Caribbean or other locations, use simple slug
    return `/${locationToSlug(location.name)}`;
  };

  const handleLocationSelect = (location) => {
    setSearchQuery("");
    setShowSuggestions(false);
    navigate(getLocationUrl(location));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim() && filteredLocations.length > 0) {
      handleLocationSelect(filteredLocations[0]);
    }
  };

  return (
    <nav className="sticky top-0 z-50 bg-sand/92 backdrop-blur-[12px] border-b border-ink/5 font-ui" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="navbar-logo">
            <img src="/logo.png" alt="Paradise Borrowed" className="h-10 w-auto" />
            <span className="font-display font-light text-lg text-ink hidden sm:block">
              Paradise Borrowed<sup className="text-[10px] ml-0.5">™</sup>
            </span>
          </Link>

          {/* Location Search Bar */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-md mx-4" ref={searchRef}>
            <div className="relative w-full">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-light" />
              <input
                data-testid="navbar-search-input"
                type="text"
                placeholder="Where are you going?"
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => searchQuery.trim() && setShowSuggestions(true)}
                className="w-full pl-10 pr-4 h-10 rounded-full border border-ink/10 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-coral-light focus:border-coral transition-all font-ui"
              />
              {showSuggestions && filteredLocations.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-ink/10 overflow-hidden z-50">
                  {filteredLocations.map((loc) => (
                    <button
                      key={loc.name}
                      type="button"
                      onClick={() => handleLocationSelect(loc)}
                      className="w-full px-4 py-3 text-left hover:bg-coral-light flex items-center gap-3 transition-colors"
                    >
                      <MapPin className="w-4 h-4 text-ink-light" />
                      <span className="text-sm text-ink">{loc.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </form>

          {/* Right Side */}
          <div className="flex items-center gap-2">
            {user && (
              <Link to="/create-listing">
                <Button variant="ghost" size="sm" className="hidden sm:flex gap-1.5 text-ink-light hover:text-ink" data-testid="navbar-create-listing">
                  <Plus className="w-4 h-4" />
                  Lend an Item
                </Button>
              </Link>
            )}

            <Link to="/how-it-works">
              <Button variant="ghost" size="sm" className="hidden sm:flex text-ink-light hover:text-ink" data-testid="navbar-how-it-works">
                How it works
              </Button>
            </Link>

            {user ? (
              <>
              {/* Notification Bell */}
              <Link to="/notifications" className="relative" data-testid="navbar-notifications-bell">
                <Button variant="ghost" size="icon" className="rounded-full w-9 h-9">
                  <Bell className="w-5 h-5 text-stone-600" />
                  {notifCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 rounded-full bg-coral text-white text-[9px] flex items-center justify-center font-bold border-2 border-sand min-w-[18px]" data-testid="notif-count-badge">{notifCount > 9 ? "9+" : notifCount}</span>
                  )}
                </Button>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="rounded-full gap-2 border-ink/10 hover:shadow-md transition-shadow relative" data-testid="navbar-user-menu">
                    <Menu className="w-4 h-4" />
                    <div className="w-6 h-6 rounded-full bg-coral flex items-center justify-center">
                      <span className="text-white text-xs font-medium">{user.name?.[0]?.toUpperCase()}</span>
                    </div>
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-coral text-white text-[9px] flex items-center justify-center font-bold border-2 border-sand" data-testid="navbar-notification-dot">{unreadCount}</span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-ink-light">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/my-profile")} data-testid="menu-profile">
                    <User className="w-4 h-4 mr-2" /> My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { navigate("/dashboard"); window.location.href = "/dashboard"; }} data-testid="menu-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/messages")} data-testid="menu-messages">
                    <MessageSquare className="w-4 h-4 mr-2" /> Messages
                    {unreadCount > 0 && (
                      <span className="ml-auto w-5 h-5 rounded-full bg-coral text-white text-[10px] flex items-center justify-center font-medium" data-testid="unread-badge">{unreadCount}</span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/create-listing")} data-testid="menu-create-listing">
                    <Plus className="w-4 h-4 mr-2" /> Lend an Item
                  </DropdownMenuItem>
                  {(user.is_admin || ADMIN_EMAILS.includes(user.email)) && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin" className="text-teal">
                        <Shield className="w-4 h-4 mr-2" /> Admin Panel
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> Log Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/auth">
                  <Button variant="ghost" size="sm" className="text-ink-light" data-testid="navbar-login">Log In</Button>
                </Link>
                <Link to="/auth?tab=register">
                  <Button size="sm" className="rounded-full bg-coral hover:bg-[#d44814] text-white btn-press transition-all" data-testid="navbar-signup">Sign Up</Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="sm:hidden" data-testid="navbar-mobile-menu">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 bg-sand">
                <SheetTitle className="font-display font-light">Menu</SheetTitle>
                <div className="flex flex-col gap-2 mt-6">
                  <div className="mb-2 relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-light" />
                    <input 
                      type="text" 
                      placeholder="Where are you going?" 
                      value={searchQuery}
                      onChange={handleSearchChange}
                      className="w-full pl-10 pr-4 h-10 rounded-xl border border-ink/10 text-sm focus:outline-none focus:ring-2 focus:ring-coral-light focus:border-coral bg-white"
                    />
                    {showSuggestions && filteredLocations.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-lg border border-ink/10 overflow-hidden z-50">
                        {filteredLocations.map((loc) => (
                          <button
                            key={loc.name}
                            type="button"
                            onClick={() => handleLocationSelect(loc)}
                            className="w-full px-4 py-2.5 text-left hover:bg-coral-light flex items-center gap-2 text-sm"
                          >
                            <MapPin className="w-3.5 h-3.5 text-ink-light" />
                            {loc.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <Link to="/how-it-works" className="py-2 px-3 rounded-lg hover:bg-white text-sm font-medium">How it works</Link>
                  {user ? (
                    <>
                      <Link to="/my-profile" className="py-2 px-3 rounded-lg hover:bg-white text-sm font-medium">My Profile</Link>
                      <Link to="/create-listing" className="py-2 px-3 rounded-lg hover:bg-white text-sm font-medium">Lend an Item</Link>
                      <Link to="/dashboard" className="py-2 px-3 rounded-lg hover:bg-white text-sm font-medium">Dashboard</Link>
                      <Link to="/messages" className="py-2 px-3 rounded-lg hover:bg-white text-sm font-medium flex items-center justify-between">
                        Messages
                        {unreadCount > 0 && (
                          <span className="w-5 h-5 rounded-full bg-coral text-white text-[10px] flex items-center justify-center font-medium">{unreadCount}</span>
                        )}
                      </Link>
                      {(user.is_admin || ADMIN_EMAILS.includes(user.email)) && (
                        <Link to="/admin" className="py-2 px-3 rounded-lg hover:bg-white text-sm font-medium text-teal">Admin Panel</Link>
                      )}
                      <button onClick={() => { logout(); navigate("/"); }} className="py-2 px-3 rounded-lg hover:bg-white text-sm font-medium text-left text-red-600">Log Out</button>
                    </>
                  ) : (
                    <>
                      <Link to="/auth" className="py-2 px-3 rounded-lg hover:bg-white text-sm font-medium">Log In</Link>
                      <Link to="/auth?tab=register" className="py-2 px-3 rounded-lg bg-coral text-white text-sm font-medium text-center">Sign Up</Link>
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
